import React, { useCallback, useEffect, useRef } from 'react';

import useChatWidgetBundle from './useChatWidgetBundle';
import useMethodicSession from './useMethodicSession';
import usePortalSessionBridge from './usePortalSessionBridge';

export interface MethodicChatGateProps {
	children: React.ReactNode;
}

/**
 * Mounts and initializes the Methodic AI chat widget, and bridges the
 * portal's OIDC session to it.
 *
 * Only renders `<methodic-widget>` once every input is final (authenticated,
 * valid session shape, bundle loaded) — the widget only reads its props once,
 * at connect time.
 */
const MethodicChatGate = ({ children }: MethodicChatGateProps) => {
	const errorTargetRef = useRef<HTMLElement | null>(null);

	const {
		methodic,
		moduleUrl,
		isEnabled,
		isActive,
		hasValidSessionShape,
		failures,
		isUsable,
		issuer,
		audience,
		agentVariant,
	} = useMethodicSession();
	const { isReady: isBundleReady } = useChatWidgetBundle(isActive);
	const shouldMount = isUsable && isBundleReady;

	useEffect(() => {
		if (!isActive || hasValidSessionShape) return;

		// eslint-disable-next-line no-console
		console.warn(
			`Methodic portal session failed shape check, skipping init: ${failures.join(', ')}`,
		);
	}, [isActive, hasValidSessionShape, failures]);

	const handleMethodicError = useCallback((event: Event) => {
		const { detail } = event as CustomEvent;
		// eslint-disable-next-line no-console
		console.error(
			detail instanceof Error
				? detail
				: new Error('Methodic widget failed to initialize', { cause: detail }),
		);
	}, []);

	// Callback ref, not a JSX `onmethodic-error` prop: React 17 doesn't
	// support custom events on custom elements.
	const attachErrorListener = useCallback(
		(node: HTMLElement | null) => {
			errorTargetRef.current?.removeEventListener(
				'methodic-error',
				handleMethodicError,
			);
			errorTargetRef.current = node;
			node?.addEventListener('methodic-error', handleMethodicError);
		},
		[handleMethodicError],
	);

	// `isEnabled`, not `shouldMount`: it must stay true through sign-out so
	// the bridge can still announce the identity change.
	usePortalSessionBridge(isEnabled);

	return (
		<>
			{children}
			{shouldMount && (
				<methodic-widget
					ref={attachErrorListener}
					moduleUrl={moduleUrl}
					apiKey={methodic?.apiKey ?? ''}
					embedType={methodic?.embedType ?? 'popup'}
					agentId={methodic?.agentId || undefined}
					agentVariant={agentVariant}
					portalAuth={JSON.stringify({ issuer, audience })}
				/>
			)}
		</>
	);
};

export default MethodicChatGate;
